import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from "rxjs";
import { odataFirst, odataGet, odataList } from "./odataApi";
import { getAuthService } from "./auth.service";
import { clearToast } from "components/ToastProvider/ToastProvider";

class ClientService {
  private _refetchAt$ = new BehaviorSubject<Date>(new Date());
  private _clientId$ = new BehaviorSubject<number | null>(null);
  private _getClientsOberservable = new Map<number, ReturnType<typeof this._getClient>>();
  public client$ = combineLatest([this._clientId$, this._refetchAt$]).pipe(
    startWith([null, this._refetchAt$.value] as [number | null, Date]),
    filter(([id]) => !!id),
    distinctUntilChanged(),
    switchMap(([id]) =>
      odataGet(
        "getLinkedClientsOne",
        { key: id! },
        {
          clientId: id!,
          errorHandler: (res) => {
            if (res.status === 404 || res.status === 403) {
              localStorage.removeItem("client_id");
              odataFirst("getClients", {}, { noClientId: true })
                .select("id")
                .orderBy("name")
                .top(1)
                .$()
                .subscribe((c) => {
                  clearToast();
                  if (c) {
                    window.location.href = window.location.origin + `/${c.id}`;
                    this.changeClientId(c.id);
                  } else {
                    getAuthService().logout();
                  }
                });
            }
          },
        },
      )
        .expandAndSelect("hubspotCompanies", null, "industry", "overdueInvoice", "hubspotId")
        .expandAndSelect("credits", null, "balance", "spend")
        .$()
        .pipe(startWith(null)),
    ),
    distinctUntilChanged(),
    shareReplay(1),
  );

  private readonly getDefaultClientId = () => {
    const split = window.location.pathname.split("/").filter((a) => !!a);
    const firstPath = split?.length ? split[0] : null;
    return (
      Number(this.clientId) ||
      Number(firstPath) ||
      Number(localStorage?.getItem && localStorage.getItem("client_id"))
    );
  };

  public defaultClientId$ = this._clientId$.pipe(
    distinctUntilChanged(),
    switchMap((clientId) =>
      clientId
        ? of(clientId)
        : this.getDefaultClientId()
          ? odataFirst(
              "getClients",
              {},
              {
                noClientId: true,
              },
            )
              .select("id")
              .orderBy("name")
              .filter("id", "eq", this.getDefaultClientId())
              .$()
              .pipe(
                switchMap((c) =>
                  !c
                    ? odataFirst("getClients", {}, { noClientId: true })
                        .select("id")
                        .orderBy("name")
                        .top(1)
                        .$()
                        .pipe(tap((c) => this.changeClientId(c.id)))
                    : of(c),
                ),
                map((c) => c.id),
              )
          : odataFirst(
              "getClients",
              {},
              {
                noClientId: true,
              },
            )
              .select("id")
              .orderBy("name")
              .top(1)
              .$()
              .pipe(
                filter((c) => !!c?.id),
                map((c) => c?.id),
                tap((id) => this.changeClientId(id)),
              ),
    ),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public getClient = (id: number | undefined) => {
    if (id === undefined) {
      return of(null);
    }
    if (!this._getClientsOberservable.has(id)) {
      this._getClientsOberservable.set(id, this._getClient(id));
    }
    return this._getClientsOberservable.get(id)!;
  };
  private readonly _getClient = (id: number) => {
    return odataGet("getClientsOne", { key: id }, { noClientId: true })
      .select("id", "name", "hsCompanyId", "tier")
      .$()
      .pipe(shareReplay(1));
  };

  public get clientId(): number | null {
    return this._clientId$.value;
  }

  constructor() {
    this._clientId$.next(this.getDefaultClientId());
    this.defaultClientId$.subscribe((cId) => cId && localStorage.setItem("client_id", cId.toString()));
  }

  public getFirst = () => odataFirst("getClients", {}, { noClientId: true });

  public changeClientId = (id: number) => {
    if (id !== this._clientId$.value) {
      this._clientId$.next(id);
      localStorage.setItem("client_id", id.toString());
    }
  };

  public extendedTeams$ = combineLatest([this._clientId$, this._refetchAt$]).pipe(
    switchMap(([clientId]) =>
      clientId
        ? odataList("getExtendedTeams", {}, { clientId })
            .select("teamId", "orgId", "enterpriseId", "orgName", "enterpriseName", "teamName")
            .orderBy("teamName")
            .$()
        : of({ value: [] }),
    ),
    map((t) => t.value),
    shareReplay(1),
  );

  public refetch = () => this._refetchAt$.next(new Date());
}

let clientService: ClientService | null = null;

export const getClientService = () => {
  if (!clientService) {
    clientService = new ClientService();
  }
  return clientService;
};

export default ClientService;
